import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { DatePicker } from '@/components/ui/date-picker';
import { StickyNote, Plus, Edit3 } from 'lucide-react';
import { toast } from 'sonner';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';

interface Family {
  id: string;
  name: string;
}

interface Student {
  id: string;
  first_name: string;
  last_name: string;
  family_id: string;
}

interface HiveNote {
  id: string;
  title: string;
  description: string | null;
  family_id: string | null;
  student_id: string | null;
  due_date: string | null;
  completed: boolean;
  archived: boolean;
  priority: 'low' | 'medium' | 'high' | 'urgent';
  created_by: string;
  organization_id: string;
  created_at: string;
  updated_at: string;
}

interface HivePadModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  editNote?: HiveNote | null;
  onSuccess?: () => void;
}

const HivePadModal: React.FC<HivePadModalProps> = ({
  open,
  onOpenChange,
  editNote = null,
  onSuccess,
}) => {
  const { organizationId, user } = useAuth();
  const [families, setFamilies] = useState<Family[]>([]);
  const [students, setStudents] = useState<Student[]>([]);
  const [filteredStudents, setFilteredStudents] = useState<Student[]>([]);
  
  // Form state
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [selectedFamilyId, setSelectedFamilyId] = useState<string>('');
  const [selectedStudentId, setSelectedStudentId] = useState<string>('');
  const [priority, setPriority] = useState<'low' | 'medium' | 'high' | 'urgent'>('medium');
  const [dueDate, setDueDate] = useState<Date | undefined>();
  const [loading, setLoading] = useState(false);
  const [familiesLoading, setFamiliesLoading] = useState(false);

  const isEditMode = Boolean(editNote);

  // Load families and students when modal opens
  useEffect(() => {
    if (open && organizationId) {
      loadFamiliesAndStudents();
    }
  }, [open, organizationId]);

  // Populate form when editing
  useEffect(() => {
    if (editNote && open) {
      setTitle(editNote.title);
      setDescription(editNote.description || '');
      setSelectedFamilyId(editNote.family_id || '');
      setSelectedStudentId(editNote.student_id || '');
      setPriority(editNote.priority || 'medium');
      setDueDate(editNote.due_date ? new Date(editNote.due_date) : undefined);
    }
  }, [editNote, open]);

  // Filter students when family changes
  useEffect(() => {
    if (selectedFamilyId) {
      const familyStudents = students.filter(student => student.family_id === selectedFamilyId);
      setFilteredStudents(familyStudents);
      // Reset student selection if current student not in new family
      if (selectedStudentId && selectedStudentId !== 'all' && !familyStudents.find(s => s.id === selectedStudentId)) {
        setSelectedStudentId('');
      }
    } else {
      setFilteredStudents([]);
      setSelectedStudentId('');
    }
  }, [selectedFamilyId, students, selectedStudentId]);

  const loadFamiliesAndStudents = async () => {
    setFamiliesLoading(true);
    try {
      // Load families
      const { data: familiesData, error: familiesError } = await supabase
        .from('families')
        .select('id, name')
        .eq('organization_id', organizationId)
        .order('name');

      if (familiesError) throw familiesError;

      // Load students
      const { data: studentsData, error: studentsError } = await supabase
        .from('students')
        .select('id, first_name, last_name, family_id')
        .eq('organization_id', organizationId)
        .order('first_name');

      if (studentsError) throw studentsError;

      setFamilies(familiesData || []);
      setStudents(studentsData || []);
    } catch (error) {
      console.error('Error loading families and students:', error);
      toast.error('Failed to load families and students');
    } finally {
      setFamiliesLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!title.trim()) {
      toast.error('Please enter a title');
      return;
    }

    setLoading(true);
    try {
      const noteData = {
        title: title.trim(),
        description: description.trim() || null,
        family_id: selectedFamilyId || null,
        student_id: selectedStudentId === 'all' ? null : selectedStudentId || null,
        priority: priority,
        due_date: dueDate?.toISOString().split('T')[0] || null,
        updated_at: new Date().toISOString(),
      };

      if (isEditMode && editNote) {
        // Update existing note
        const { error } = await supabase
          .from('hive_notes')
          .update(noteData)
          .eq('id', editNote.id);

        if (error) throw error;
        toast.success('Note updated successfully!');
      } else {
        // Create new note
        const { error } = await supabase
          .from('hive_notes')
          .insert([{
            ...noteData,
            completed: false,
            archived: false,
            created_by: user?.id,
            organization_id: organizationId,
          }]);

        if (error) throw error;
        toast.success('Note added to HivePad!');
      }

      onSuccess?.();
      handleClose();
    } catch (error) {
      console.error('Error saving note:', error);
      toast.error(`Failed to ${isEditMode ? 'update' : 'create'} note`);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    // Reset form
    setTitle('');
    setDescription('');
    setSelectedFamilyId('');
    setSelectedStudentId('');
    setPriority('medium');
    setDueDate(undefined);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <StickyNote className="h-5 w-5 text-orange-600" />
            {isEditMode ? 'Edit Note' : 'Add to HivePad'}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Title Field */}
          <div className="space-y-2">
            <Label htmlFor="title">Title *</Label>
            <Input
              id="title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Enter a title for this note..."
              required
            />
          </div>

          {/* Description Field */}
          <div className="space-y-2">
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Add details or notes..."
              rows={3}
            />
          </div>

          {/* Priority Select */}
          <div className="space-y-2">
            <Label htmlFor="priority">Priority</Label>
            <Select value={priority} onValueChange={(value: 'low' | 'medium' | 'high' | 'urgent') => setPriority(value)}>
              <SelectTrigger>
                <SelectValue placeholder="Select priority..." />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="low">
                  <span className="flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-gray-400"></span>
                    Low Priority
                  </span>
                </SelectItem>
                <SelectItem value="medium">
                  <span className="flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-blue-500"></span>
                    Medium Priority
                  </span>
                </SelectItem>
                <SelectItem value="high">
                  <span className="flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-orange-500"></span>
                    High Priority
                  </span>
                </SelectItem>
                <SelectItem value="urgent">
                  <span className="flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-red-500"></span>
                    Urgent Priority
                  </span>
                </SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Family Select */}
          <div className="space-y-2">
            <Label htmlFor="family">Family (Optional)</Label>
            <Select value={selectedFamilyId} onValueChange={setSelectedFamilyId}>
              <SelectTrigger>
                <SelectValue placeholder="Select a family..." />
              </SelectTrigger>
              <SelectContent>
                {families.map((family) => (
                  <SelectItem key={family.id} value={family.id}>
                    {family.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Student Select - Only show if family is selected */}
          {selectedFamilyId && (
            <div className="space-y-2">
              <Label htmlFor="student">Student (Optional)</Label>
              <Select value={selectedStudentId} onValueChange={setSelectedStudentId}>
                <SelectTrigger>
                  <SelectValue placeholder="Select a student..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Students in Family</SelectItem>
                  {filteredStudents.map((student) => (
                    <SelectItem key={student.id} value={student.id}>
                      {student.first_name} {student.last_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Due Date */}
          <div className="space-y-2">
            <Label htmlFor="due-date">Due Date (Optional)</Label>
            <DatePicker
              value={dueDate}
              onChange={setDueDate}
              placeholder="Select a due date..."
              minDate={new Date()}
            />
          </div>

          {/* Actions */}
          <div className="flex gap-2 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={loading || !title.trim()}
              className="flex-1"
            >
              {loading ? (
                `${isEditMode ? 'Updating' : 'Adding'}...`
              ) : (
                <>
                  {isEditMode ? (
                    <>
                      <Edit3 className="h-4 w-4 mr-1" />
                      Update Note
                    </>
                  ) : (
                    <>
                      <Plus className="h-4 w-4 mr-1" />
                      Add Note
                    </>
                  )}
                </>
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default HivePadModal;